import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { MdHome, MdPermIdentity, MdEmail, MdPerson, MdInfo } from 'react-icons/md';
import { Form } from '@unform/web';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingContext from '../../../contexts/LoadingContext';
import api from '../../../service/api';
import Input from '../../../components/Input';
import { YupShape, YupString, YupStringRequired, YupUF, YupCpfCnpj, YupCep, YupEmail, YupTelefone, YupConexoes, YupValidationError } from '../../../utils/schemas';
import { FormHandles } from '@unform/core';
import getValidationErrors from '../../../utils/getValidationErrors';
import { useToast } from '../../../contexts/ToastContext';
import { RevisarDadosFormData, gerarPayload } from './utils/gerarPayload';
import ListaDeContextos from './utils/listaDeContextos';

import './styles.css';

const RevisarDados: React.FC = () => {
  const { jwt, signOut } = useAuth();
  api.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      if (error.message === 'Request failed with status code 401') {
        signOut();
      } else {
        return error;
      }
    }
  );
  if (jwt) api.defaults.headers = { Authorization: `Bearer ${jwt}` };
  const formRef = useRef<FormHandles>(null);
  const history = useHistory();
  const { clienteId, env } = useParams<{clienteId: string, env: string}>();
  const { changeLoadingState, changeLoadingMessage } = useContext(LoadingContext);
  const [cliente,setCliente] = useState<any>({});
  const [cpfCnpj,setCpfCnpj] = useState<string>('');
  const [cep,setCep] = useState<string>('');
  const { addToast } = useToast();
  
  useEffect(() => {
    async function getDadosImob(){
      try{
        changeLoadingState(true);
        const resposta = await api.get(
          `/listar-dados-imob/${clienteId}`
        );
        setCliente(resposta.data);
        setCpfCnpj(formatCpfCnpj(resposta.data.identidade.cnpjcpf));
        setCep(formatCep(resposta.data.endereco.cep));
        if(formRef.current) formRef.current.setData(resposta.data);
        changeLoadingState(false);
      } catch (e) {
        console.log(e);
        changeLoadingState(false);
      }
    }
    getDadosImob();
    // eslint-disable-next-line
  },[clienteId]) 

  const handleSubmit = useCallback(async (data: RevisarDadosFormData) => {
    try{
      formRef.current?.setErrors({});
      const formData = {
        ...data,
        identidade: {
          ...data.identidade,
          cnpjcpf: data.identidade.cnpjcpf.replace(/\D/g, ''),
        },
        endereco: {
          ...data.endereco,
          cep: data.endereco.cep.replace(/\D/g, ''),
        },
      };
      const schema = YupShape({
        identidade: YupShape({
          razaoSocial: YupStringRequired('Campo obrigatório'),
          nomeFantasia: YupStringRequired('Campo obrigatório'),
          cnpjcpf: YupCpfCnpj(),
        }),
        endereco: YupShape({
          logradouro: YupStringRequired('Campo obrigatório'),
          numero: YupString(),
          bairro: YupStringRequired('Campo obrigatório'),
          complemento: YupString(),
          municipio: YupStringRequired('Campo obrigatório'),
          uf: YupUF(),
          cep: YupCep(),         
        }),
        dadosInternet: YupShape({
          email: YupEmail('E-mail inválido','Campo obrigatório')
        }),
        responsavelEmpresa: YupShape({
          nome: YupStringRequired('Campo obrigatório'),
          cpf: YupCpfCnpj(true),
          telefoneFixo: YupTelefone(),
          telefoneCelular: YupTelefone(),
          email: YupEmail('E-mail inválido','Campo obrigatório'),
          senha: YupStringRequired('Campo obrigatório')
        }),
        contratos: YupShape({
          qtdeConexoes: YupConexoes(),
        })
      });
      await schema.validate(formData, {abortEarly: false});
      const json = gerarPayload(formData,cliente.codigo);
      changeLoadingState(true);
      changeLoadingMessage('Criando imobiliária no Identidade e Acesso');
      const respostaIa = await api.post(`/criar-tenant/${env}/identidade-acesso`,json);
      if(!respostaIa || !respostaIa.data || !respostaIa.data.tenantId){
        throw new Error();
      }
      const {tenantId} = respostaIa.data;
      for(const contexto of ListaDeContextos){
        changeLoadingMessage(`Criando ambiente ${contexto}`);
        await api.post(`/criar-tenant/${env}/${contexto}`,{tenantId});
      }
      changeLoadingMessage('');
      changeLoadingState(false);
      addToast({
        type: 'success',
        title: 'Ambientes criados',
        description:
          'Os ambientes foram criados com sucesso',
      });
      history.goBack();
    }catch(e){
      console.log(e);
      changeLoadingState(false);
      if (e instanceof YupValidationError) {
        const unformErrors = getValidationErrors(e);
        formRef.current?.setErrors(unformErrors);
        addToast({
          type: 'error',
          title: 'Erro na validação',
          description:
            'Verifique os campos do formulário',
        });
      }else{
        addToast({
          type: 'error',
          title: 'Erro ao criar ambientes',
          description:
            'Tente novamente mais tarde',
        });
      }
    }
    // eslint-disable-next-line
  },[cliente.codigo])

  function formatCpfCnpj(value: string){
    if(!value) return '';
    const unmask = value.replace(/\D/g,'');
    const mask = unmask.match(/((^\d{3})(\d{3})(\d{3})(\d{2}$))|((^\d{2})(\d{3})(\d{3})(\d{4})(\d{2}$))/);
    if(!mask) {
      return value;
    }
    let retorno = '';
    if(unmask.length > 11){
      retorno = `${mask[7]}.${mask[8]}.${mask[9]}/${mask[10]}-${mask[11]}`
    }else{
      retorno = `${mask[2]}.${mask[3]}.${mask[4]}-${mask[5]}`;
    }
    return retorno;
  }

  const handleCpfCnpjChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCpfCnpj(formatCpfCnpj(e.target.value));
  },[])

  function formatCep(value: string){
    if(!value) return '';
    const unmask = value.replace(/\D/g,'');
    const mask = value.match(/(^\d{5})(\d{3})$/);
    if(!mask){
      return value;
    }
    let retorno = unmask;
    if(unmask.length === 8){
      retorno = `${mask[1]}-${mask[2]}`
    }
    return retorno;
  }

  const handleCepChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCep(formatCep(e.target.value));
  },[])

  return (
    <div className='container'>
      <div className="voltar-container">
        <button type='button' onClick={history.goBack}>
          Voltar
        </button>
        <p>Código: {cliente.codigo}</p>
      </div>
      <Form ref={formRef} onSubmit={handleSubmit}>
        <div className="form-sessao">
          <header className="form-sessao-header">
            <MdPermIdentity size={24} />
            <p>Identidade</p>
          </header>
          <div className="form-input-grid">
            <div className="revisar-input-group">
              <label>Razão Social</label>
              <Input type="text" name="identidade.razaoSocial" />
            </div>
            <div className="revisar-input-group">
              <label>Nome Fantasia</label>
              <Input type="text" name="identidade.nomeFantasia" />
            </div>
            <div className="revisar-input-group">
              <label>CPF/CNPJ</label>
              <Input type="text" name="identidade.cnpjcpf" value={cpfCnpj} onChange={(e) => handleCpfCnpjChange(e)} />
            </div>
          </div> 
        </div>
        <div className="form-sessao">
          <header className="form-sessao-header">
            <MdHome size={24} />
            <p>Endereço</p>
          </header>
          <div className="form-input-grid">
            <div className="revisar-input-group">
              <label>Logradouro</label>
              <Input type="text" name="endereco.logradouro" />
            </div>
            <div className="revisar-input-group">
              <label>Número</label>
              <Input style={{width: '120px'}} type="text" name="endereco.numero" />
            </div>
            <div className="revisar-input-group">
              <label>Bairro</label>
              <Input type="text" name="endereco.bairro" />
            </div>
            <div className="revisar-input-group">
              <label>Complemento</label>
              <Input type="text" name="endereco.complemento" />
            </div>
            <div className="revisar-input-group">
              <label>Município</label>
              <Input type="text" name="endereco.municipio" />
            </div>
            <div className="revisar-input-group">
              <label>Estado</label>
              <Input style={{width: '55px'}} type="text" name="endereco.uf" />
            </div>
            <div className="revisar-input-group">
              <label>CEP</label>
              <Input style={{width: '120px'}} type="text" name="endereco.cep" value={cep} onChange={(e) => handleCepChange(e)} />
            </div>
          </div>
        </div>
        <div className="form-sessao">
          <header className="form-sessao-header">
            <MdEmail size={24} />
            <p>Dados Internet</p>
          </header>
          <div className="revisar-input-group">
            <label>E-mail</label>
            <Input style={{width: '30%'}} type="text" name="dadosInternet.email" />
          </div>
        </div>
        <div className="form-sessao">
          <header className="form-sessao-header">
            <MdPerson size={24} />
            <p>Responsável</p>
          </header>
          <span style={{ fontSize: '1.5rem', color: '#009688' }}>Esse usuário será criado no Identidade e Acesso como administrador</span>
          <div className="form-input-grid">
            <div className="revisar-input-group">
              <label>Nome</label>
              <Input type="text" name="responsavelEmpresa.nome" />
            </div>
            <div className="revisar-input-group">
              <label>CPF</label>
              <Input type="text" name="responsavelEmpresa.cpf" />
            </div>
            <div className="revisar-input-group">
              <label>Telefone Fixo</label>
              <Input type="text" name="responsavelEmpresa.telefoneFixo" />
            </div>
            <div className="revisar-input-group">
              <label>Celular</label>
              <Input type="text" name="responsavelEmpresa.telefoneCelular" />
            </div>
            <div className="revisar-input-group">
              <label>E-mail</label>
              <Input type="text" name="responsavelEmpresa.email" />
            </div>
            <div className="revisar-input-group">
              <label>Senha</label>
              <Input type="text" name="responsavelEmpresa.senha" defaultValue="123456" />
            </div>
          </div>
        </div>
        <div className="form-sessao">
          <header className="form-sessao-header">
            <MdInfo size={24} />
            <p>Informações Contratuais</p>
          </header>
          <div className="revisar-input-group">
            <label>Total de conexões</label>
            <Input style={{width: '80px'}} type="text" name="contratos.qtdeConexoes" />
          </div>
        </div>
        <button type='submit' className="criar-ambiente-btn">
          Criar ambiente
        </button>
      </Form>
    </div>
  );
};

export default RevisarDados;
